import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { cn } from '@plato/ui/util/classes';
import { Root } from '@radix-ui/react-slot';
import type { ReactElement, ReactNode } from 'react';

export type LineItemRowBaseProps = {
    asChild?: boolean;
    children?: ReactNode;
    className?: string;
    header?: boolean;
};

export function LineItemRowBase({
    asChild,
    children,
    className,
    header,
    ...rest
}: LineItemRowBaseProps): ReactElement {
    const Comp = asChild ? Root : 'div';
    const optionalColumnCount = useDraftItemOptionalColumnCount();
    const gridTemplateColumns = `3fr repeat(${optionalColumnCount + 2}, 1fr) calc(var(--spacing) * 10)`;

    return (
        <Comp
            className={cn(
                'shrink-0 sm:grid sm:min-h-10 sm:gap-3 sm:px-3 sm:py-2 border-neutral-100',
                header
                    ? // Desktop-only table header, divided from the rows by a bottom border.
                      'hidden bg-neutral-25 text-neutral-600 sm:items-center sm:border-b'
                    : // Mobile: a bordered card. Desktop: a plain row with a bottom divider (except the last).
                      'flex flex-col rounded-md border sm:rounded-none sm:border-0 sm:border-b sm:last:border-0',
                className,
            )}
            style={{ gridTemplateColumns }}
            role="row"
            {...rest} // this is used to pass along a data-testid if provided
        >
            {children}
        </Comp>
    );
}

export type LineItemRowCellProps = {
    children?: ReactNode;
    className?: string;
    header?: boolean;
    /** Field label shown only on mobile, where the column header is hidden. */
    label?: string;
    right?: boolean;
    span?: number;
};

export function LineItemRowCell({
    children,
    className,
    header,
    label,
    right,
    span = 1,
}: LineItemRowCellProps): ReactElement {
    return (
        <div
            className={cn('flex flex-col gap-1', right && 'text-right', className)}
            style={{ gridColumnEnd: `span ${span}` }}
            role={header ? 'columnheader' : 'cell'}
        >
            {label && <span className="text-xs text-neutral-500 sm:hidden">{label}</span>}
            {children}
        </div>
    );
}

export function useDraftItemOptionalColumnCount(): number {
    const customerViewToggled = useIsCustomerMode();
    const { canReadGrossMargin, canReadTransactionGrossMargin, canReadLineItemRebate } = useAppConfig();
    return (
        (canReadGrossMargin && canReadTransactionGrossMargin && !customerViewToggled ? 1 : 0) +
        (canReadLineItemRebate ? 1 : 0)
    );
}
